import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import { KEY_WARENKORB, type WarenkorbAntwort } from "./api";
import type { RueckgabeVorschau, SuchTreffer } from "../../worker/data/kasse-sonderfaelle";

export type { RueckgabeVorschau, SuchTreffer };
export type RabattGrund = "maengel" | "mengenrabatt" | "kulanz" | "aktion";
export const RABATT_GRUND_TEXT: Record<RabattGrund, string> = { maengel: "Mängel", mengenrabatt: "Mengenrabatt", kulanz: "Kulanz", aktion: "Aktion" };

export function useRabatt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; pos: string; art: "prozent" | "preis"; wert: number; grund: RabattGrund; freigabe?: { benutzerId: string; pin: string } }) =>
      rufe<WarenkorbAntwort & { prozent: number }>(api.api.kasse.warenkorb[":id"].positionen[":pos"].rabatt.$post({ param: { id: v.id, pos: v.pos }, json: { art: v.art, wert: v.wert, grund: v.grund, freigabe: v.freigabe } })),
    onSuccess: (d) => qc.setQueryData(KEY_WARENKORB, (alt: WarenkorbAntwort | undefined) => ({ warenkorb: d.warenkorb, kandidaten: alt?.kandidaten ?? null })),
  });
}

export function useSuche(q: string, art: "regal" | "mieter" | "bezeichnung", status: "im_regal" | "verkauft" = "im_regal") {
  return useQuery({
    queryKey: ["kasse", "suche", art, q, status],
    queryFn: () => rufe<{ treffer: SuchTreffer[] }>(api.api.kasse.suche.$get({ query: { q, art, status } })),
    enabled: q.trim().length > 0,
    staleTime: 5_000,
  });
}

export function useRueckgabeVorschau(code: string | null) {
  return useQuery({
    queryKey: ["kasse", "rueckgabe", code],
    queryFn: () => rufe<RueckgabeVorschau>(api.api.kasse.rueckgabe[":code"].$get({ param: { code: code! } })),
    enabled: !!code,
    retry: false,
  });
}

export function useRueckgabeBuchen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { code: string; grund: "defekt" | "gefaellt_nicht" | "umtausch"; erstattungArt: "sumup" | "bar"; notiz: string; inSumupErstattet: boolean }) => rufe<{ ok: true; betragCent: number; rueckgabeId: string }>(api.api.kasse.rueckgabe.$post({ json: v })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["kasse"] }),
  });
}

export interface Kachel {
  id: string;
  name: string;
  preisCent: number | null;
  icon: string;
}

export function useKacheln() {
  return useQuery({ queryKey: ["kasse", "ladenware"], queryFn: () => rufe<{ kacheln: Kachel[] }>(api.api.kasse.ladenware.$get()), staleTime: 60_000 });
}

export function useKachelHinzufuegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; kachelId: string; preisCent?: number }) => rufe<WarenkorbAntwort>(api.api.kasse.warenkorb[":id"].ladenware.kachel.$post({ param: { id: v.id }, json: { kachelId: v.kachelId, preisCent: v.preisCent } })),
    onSuccess: (d) => qc.setQueryData(KEY_WARENKORB, (alt: WarenkorbAntwort | undefined) => ({ warenkorb: d.warenkorb, kandidaten: alt?.kandidaten ?? null })),
  });
}
